/**
 * Custom element for the email signup form, handles custom form submission and validation.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} emailInput
 * @property {HTMLInputElement} nameInput
 * @property {HTMLElement} emailError
 * @property {HTMLElement} emailErrorText
 *
 * @extends {HTMLElement}
 */

class EmailSignupForm extends HTMLElement {
    constructor() {
        super();
        this.init();
    }

    /**
     * Initializes the form and email input
     */
    init() {
        this.form = this.querySelector('form');
        this.emailInput = this.querySelector('input[type="email"]');
        this.nameInput = this.querySelector('input[name="contact[name]"]');
        this.emailError = this.querySelector('.email-signup__message--error');
        this.emailSuccess = this.querySelector('.email-signup__message--success');
        this.emailErrorText = this.querySelector('.email-signup__message-text');

        if (!this.form || !this.emailInput || !this.emailError || !this.emailErrorText) {
            return;
        }
        
        this.attachEventListeners();
    }


    /**
     * Attaches event listeners to the form and email input
     */
    attachEventListeners() {
        this.form?.addEventListener('submit', this.handleSubmit.bind(this));
        this.emailInput?.addEventListener('input', this.clearError.bind(this));
        this.nameInput?.addEventListener('input', this.clearError.bind(this));
    }

    /**
     * Handles form submission and validates email input
     * @param {Event} e - The form submit event
     */
    handleSubmit(e) {
        const emailValue = /** @type {HTMLInputElement} */ (this.emailInput)?.value;
        const nameValue = /** @type {HTMLInputElement} */ (this.nameInput)?.value;

        if (this.emailSuccess) this.emailSuccess.classList.add("email-signup-message--hidden");
        
        // Check if name field is required and validate it
        if (this.nameInput && this.nameInput.hasAttribute('required') && !this.isValidName(nameValue)) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.nameInput?.setAttribute('aria-invalid', 'true');
            this.showError("Please enter your name.");
            return;
        }
        
        // Validate email
        if (!this.isValidEmail(emailValue)) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.emailInput?.setAttribute('aria-invalid', 'true');
            this.showError("Email address not valid.");
            return;
        }

        if (this.emailInput) this.emailInput.setAttribute('aria-invalid', 'false');
        if (this.nameInput) this.nameInput.setAttribute('aria-invalid', 'false');
    }

    /**
     * Validates email address using a comprehensive regex pattern
     * @param {string} email - The email address to validate
     * @returns {boolean} - True if email is valid, false otherwise
     */
    isValidEmail(email) {
        if (!email || typeof email !== 'string') {
            return false;
        }
        const emailRegex = /^[a-zA-Z0-9]([a-zA-Z0-9._-]*[a-zA-Z0-9])?@[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?\.[a-zA-Z]{2,}$/;
        
        return emailRegex.test(email.trim());
    }

    /**
     * Validates name field - checks if it's not empty and has reasonable length
     * @param {string} name - The name to validate
     * @returns {boolean} - True if name is valid, false otherwise
     */
    isValidName(name) {
        if (!name || typeof name !== 'string') {
            return false;
        }
        const trimmedName = name.trim();
        return trimmedName.length >= 2 && trimmedName.length <= 100;
    }

    /**
     * Shows error message to the user
     * @param {string} message - The error message to display
     */
    showError(message) {
        if (this.emailErrorText) this.emailErrorText.textContent = message;
        if (this.emailError) this.emailError.classList.remove("email-signup-message--hidden");
    }

    clearError() {
        if (this.emailError) this.emailError.classList.add("email-signup-message--hidden");
        if (this.emailSuccess) this.emailSuccess.classList.add("email-signup-message--hidden");
        if (this.emailInput) this.emailInput.removeAttribute('aria-invalid');
        if (this.nameInput) this.nameInput.removeAttribute('aria-invalid');
    }
}

customElements.define('email-signup-form', EmailSignupForm);